import { Component, ReactNode, memo, useRef, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { Icon } from '@iconify/react';
import useUserStore from '../../../../common/src/stores/useUserStore';
import { getChartThemeColors } from '../../../../common/src/utils/chartTheme';
import ChartDownloadModal from '../../../../common/src/components/charts/ChartDownloadModal';
import { ChartSpec } from '../../hooks/useVictoriaChat';

// Same palette family as the platform charts (USDA explorer / retail).
const SERIES_COLORS = [
  '#e11d48',
  '#2563eb',
  '#16a34a',
  '#9333ea',
  '#ea580c',
  '#0891b2',
];

// A broken spec must never leave a silent empty box (or crash the widget).
class ChartBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (this.state.failed) {
      return (
        <p className='text-[11px] text-default-400 py-2'>
          No pude renderizar el gráfico.
        </p>
      );
    }
    return this.props.children;
  }
}

function VictoriaChartInner({ spec }: { spec: ChartSpec }) {
  const { darkMode } = useUserStore();
  const themeColors = getChartThemeColors(darkMode);
  const [previewOpen, setPreviewOpen] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);

  // Bar charts choke on null datapoints (lines/areas render them as gaps) —
  // and guard against non-numeric values slipping through.
  const series = spec.series.map((s) => ({
    name: s.name,
    data: s.data.map((v) =>
      typeof v === 'number' && isFinite(v) ? v : spec.type === 'bar' ? 0 : null
    ),
  }));

  const options: ApexOptions = {
    chart: {
      type: spec.type,
      background: 'transparent',
      foreColor: themeColors.foreColor,
      redrawOnParentResize: true,
      // No apex toolbar: fullscreen + download live in the preview modal.
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    colors: SERIES_COLORS,
    stroke: { curve: 'smooth', width: spec.type === 'bar' ? 0 : 2 },
    ...(spec.type === 'area' && { fill: { opacity: 0.25 } }),
    dataLabels: { enabled: false },
    grid: { borderColor: themeColors.gridBorderColor },
    xaxis: {
      categories: spec.categories,
      labels: { style: { fontSize: '10px' }, rotate: -30 },
    },
    yaxis: {
      title: spec.unit
        ? { text: spec.unit, style: { fontSize: '10px', fontWeight: 500 } }
        : undefined,
      labels: {
        formatter: (v: number) =>
          v == null ? '' : v.toLocaleString('es', { maximumFractionDigits: 2 }),
      },
    },
    legend: { position: 'bottom', fontSize: '11px' },
    title: {
      text: spec.title,
      style: { fontSize: '12px', fontWeight: 600 },
    },
    tooltip: { theme: darkMode ? 'dark' : 'light' },
  };

  // Full-size copy the preview modal captures (html2canvas on the ref) —
  // rendered off-screen only while the modal is open, so the preview and the
  // downloaded PNG come out crisp instead of bubble-sized.
  const previewOptions: ApexOptions = {
    ...options,
    chart: {
      ...options.chart,
      animations: { enabled: false },
      toolbar: { show: false },
    },
  };

  return (
    <>
      <div className='relative mt-2 rounded-lg border border-default-100 bg-background px-1 pt-1'>
        <button
          onClick={() => setPreviewOpen(true)}
          title='Ver en pantalla completa'
          className='absolute top-1.5 right-1.5 z-10 p-1 rounded-md text-default-400 hover:text-foreground hover:bg-default-100 transition-colors'
        >
          <Icon icon='tabler:arrows-maximize' width={14} />
        </button>
        <ReactApexChart
          options={options}
          series={series}
          type={spec.type}
          height={235}
        />
      </div>
      {previewOpen && (
        <>
          <div
            ref={previewRef}
            className='fixed top-0 -left-[10000px] w-[860px] p-3'
            style={{ background: darkMode ? '#18181b' : '#ffffff' }}
          >
            <ReactApexChart
              options={previewOptions}
              series={series}
              type={spec.type}
              height={430}
            />
          </div>
          <ChartDownloadModal
            isOpen={previewOpen}
            onClose={() => setPreviewOpen(false)}
            chartRef={previewRef}
            title={spec.title}
            subtitle={spec.unit}
            classNames={{ wrapper: 'z-[10001]', backdrop: 'z-[10000]' }}
          />
        </>
      )}
    </>
  );
}

// Text deltas re-render the whole message list on every chunk; the spec object
// reference is stable, so memo keeps ApexCharts from re-rendering mid-stream.
const MemoChart = memo(
  VictoriaChartInner,
  (prev, next) => prev.spec === next.spec
);

export default function VictoriaChart({ spec }: { spec: ChartSpec }) {
  return (
    <ChartBoundary>
      <MemoChart spec={spec} />
    </ChartBoundary>
  );
}
